import React, { useState } from 'react';
import styled from 'styled-components';

import { FlexColumn, Input, Size, SvgIcon } from '@make-software/cspr-ui';
import { Button, BodyText } from '@block-explorer/ui';

import { useCustomHistory } from '../../router/utils';
import { useSearch } from '../../hooks/use-search';

const StyledContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const SearchButton = styled(Button)(({ theme }) => ({
  padding: 0,
  position: 'absolute',
  right: 0,
  top: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  minWidth: 40,
  margin: 0,
}));

const StyledInput = styled(Input)(({ theme }) => ({
  paddingRight: 35,
}));

const NothingFoundMenu = styled(FlexColumn)<
  Pick<SearchInputProps, 'top' | 'width'>
>(({ theme, top, width }) =>
  theme.withMedia({
    width: width,
    background: theme.styleguideColors.backgroundPrimary,
    borderRadius: theme.borderRadius.base,
    position: 'absolute',
    top: top,
    height: 149,
    zIndex: theme.zIndex.dropdown,
    boxShadow: theme.boxShadow.tooltip,
    justifyContent: 'center',
    alignItems: 'center',

    path: {
      fill: theme.styleguideColors.fillPrimaryRed,
    },
  })
);

export interface SearchInputProps {
  className?: string;
  innerRef?: any;
  disabled?: boolean;
  required?: boolean;
  size?: Size;
  placeholder?: string;
  value?: any;
  onChange?: (ev: any) => void;
  onKeyDown?: (ev: any) => void;
  onSearchSubmit?: () => void;
  top?: (string | number)[];
  width?: (string | number)[];
}

export function SearchInput({
  top,
  width,
  onSearchSubmit,
  ...props
}: SearchInputProps) {
  const history = useCustomHistory();
  const [searchValue, setSearchValue] = useState('');
  const [showNotFoundMenu, setShowNotFoundBlock] = useState(false);

  const { data, error } = useSearch(searchValue.trim().toLowerCase());

  const handleSearchSubmit = () => {
    if (data) {
      const { hash, type } = data;

      history.push(`/${type}/${hash}`);

      if (onSearchSubmit) {
        onSearchSubmit();
      }
    } else {
      setShowNotFoundBlock(!!error && searchValue !== '');
    }
  };

  return (
    <StyledContainer>
      <StyledInput
        height="40"
        placeholder="Search by public key, hash, uref or CSPR.name"
        onChange={(event) => {
          setSearchValue(event.target.value);
          setShowNotFoundBlock(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleSearchSubmit();
          }
        }}
        {...props}
      />
      <SearchButton height="40" onClick={handleSearchSubmit} color="primaryRed">
        <SvgIcon src="assets/icons/ic-search.svg" size={16} />
      </SearchButton>
      {showNotFoundMenu && (
        <NothingFoundMenu itemsSpacing={16} top={top} width={width}>
          <SvgIcon src="assets/icons/ic-search.svg" size={40} />
          <BodyText size={3}>Nothing was found...</BodyText>
        </NothingFoundMenu>
      )}
    </StyledContainer>
  );
}
